import React from 'react';
import { Field, FieldProps } from 'formik';

import {
  AccessibleFormItemStyled,
  FieldErrorAlert,
  FieldErrorText,
  FieldHintText,
  InputStyled,
} from './AccessibleInputField.style';

interface Props {
  name: string;
  label: string;
  required?: boolean;
  validate?: (value: string) => string | undefined;
  hint?: string;
  placeholder?: string;
  autoComplete?: string;
  hidden?: boolean;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  tooltip?: React.ReactNode;
  transformValue?: (value: string) => string;
}

const buildDescribedBy = (hasHint: boolean, hintId: string, errorId: string): string => {
  if (hasHint) {
    return `${hintId} ${errorId}`;
  }
  return errorId;
};

export const AccessibleInputField: React.FunctionComponent<Props> = ({
  name,
  label,
  required,
  validate,
  hint,
  placeholder,
  autoComplete,
  hidden,
  style,
  inputStyle,
  tooltip,
  transformValue,
}: Props) => {
  const hintId = `${name}-hint`;
  const errorId = `${name}-error`;

  return (
    <Field name={name} validate={validate}>
      {({ field, form, meta }: FieldProps<string>) => {
        const normalizedHint = typeof hint === 'string'
          ? hint.trim()
          : '';
        const hasHint = normalizedHint.length > 0;
        const hasError = meta.touched === true && typeof meta.error === 'string' && meta.error.length > 0;
        const errorMessage = hasError
          ? meta.error
          : '';
        const describedByIds = buildDescribedBy(hasHint, hintId, errorId);

        return (
          <AccessibleFormItemStyled
            label={label}
            required={required}
            htmlFor={name}
            labelCol={{ span: 24 }}
            validateStatus={hasError
              ? 'error'
              : undefined}
            help={null}
            hidden={hidden}
            style={style}
            tooltip={tooltip}
          >
            <InputStyled
              {...field}
              name={name}
              id={name}
              style={inputStyle}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-invalid={hasError}
              aria-describedby={describedByIds}
              onChange={(event) => {
                const transformedValue = transformValue === undefined
                  ? event.target.value
                  : transformValue(event.target.value);
                form.setFieldValue(name, transformedValue);
              }}
            />
            {hasHint &&
              <FieldHintText id={hintId}>{normalizedHint}</FieldHintText>
            }
            <FieldErrorAlert role="alert" aria-live="assertive" aria-atomic="true">
              <FieldErrorText id={errorId}>{errorMessage}</FieldErrorText>
            </FieldErrorAlert>
          </AccessibleFormItemStyled>
        );
      }}
    </Field>
  );
};
